<script setup>
import { ref, onMounted } from 'vue'
import Keycloak from 'keycloak-js'
import JsCookie from 'js-cookie'
import { loadLanguageAsync } from './plugins/i18n'

const keycloakBaseUrl = window.app.login.keycloakBaseUrl
const keycloakRealm = window.app.login.keycloakRealm
const keycloakApplicationId = window.app.login.keycloakApplicationId
const keycloakLocale = window.app.login.keycloakLocale
const privacyurl = window.app.login.privacylink
const dashboardurl = window.app.login.dashboardlink

const keycloak = ref({})
const location = window.location.origin + window.location.pathname
const isAuthenticated = ref(false)
const options = ref({})
const timeout = 5000
const username = ref('')
const interval = ref(null)

const einhellAuthDataOptions = {
  expires: 7,
  domain: '.' + window.app.login.syliusCookieDomain,
  secure: true
}

const einhellLoginDataOptions = {
  expires: 1,
  domain: '.' + window.app.login.syliusCookieDomain,
  secure: true
}

function createKeycloak() {
  keycloak.value = new Keycloak({
    url: keycloakBaseUrl,
    realm: keycloakRealm,
    clientId: keycloakApplicationId
  })
}

function setOptions(data) {
  options.value.token = data.token
  options.value.refreshToken = data.refresh_token
  options.value.idToken = data.id_token
}

function setUsername() {
  username.value = keycloak.value.idTokenParsed.given_name + ' ' + keycloak.value.idTokenParsed.family_name
}

function writeDataToLocalStorage() {
  const ls = window.localStorage
  const newEinhellAuthDataObject = {
    token: keycloak.value.token,
    refresh_token: keycloak.value.refreshToken,
    id_token: keycloak.value.idToken,
    changed_at: Math.floor(Date.now() / 1000)
  }
  ls.setItem('einhell-auth-data', JSON.stringify(newEinhellAuthDataObject))

  const cookieObject = {
    token: keycloak.value.token,
    changed_at: Math.floor(Date.now() / 1000)
  }
  const cookieIdTokenObject = {
    id_token: keycloak.value.idToken
  }
  JsCookie.set('einhell-auth-data', JSON.stringify(cookieObject), einhellAuthDataOptions)
  JsCookie.set('einhell-logout-data', JSON.stringify(cookieIdTokenObject), einhellAuthDataOptions)

  const newEinhellLoginDataObject = {
    dashboard_url: dashboardurl,
    privacy_url: privacyurl,
    base_uri: location,
    accountmanager_url: keycloak.value.createAccountUrl({ redirectUri: location }) + '&kc_locale=' + keycloakLocale + '&privacy_url=' + privacyurl
  }
  JsCookie.set('einhell-login-data', JSON.stringify(newEinhellLoginDataObject), einhellLoginDataOptions)
}

function deleteAllData() {
  clearInterval(interval.value)
  keycloak.value.clearToken()
  const ls = window.localStorage
  ls.removeItem('einhell-auth-data')
  JsCookie.remove('einhell-logout-data', einhellAuthDataOptions)
  JsCookie.remove('einhell-auth-data', einhellAuthDataOptions)
  JsCookie.remove('einhell-login-data', einhellLoginDataOptions)
  JsCookie.remove('checkout_loggedin')
  isAuthenticated.value = false
  ls.setItem('einhell-login-data', JSON.stringify({ loginUrl: createLoginUrl() }))
  const newEinhellLoginDataObject = {
    dashboard_url: dashboardurl,
    privacy_url: privacyurl,
    base_uri: location
  }
  JsCookie.set('einhell-login-data', JSON.stringify(newEinhellLoginDataObject), einhellLoginDataOptions)
}

function deleteAllDataAfterLogout() {
  deleteAllData()
}

function checkToken() {
  keycloak.value.updateToken(timeout / 1000 + 1).then(refreshed => {
    if (refreshed) {
      writeDataToLocalStorage()
      isAuthenticated.value = refreshed
      if (isAuthenticated.value) {
        window.keycloak.isAuthenticated = true
        setUsername()
      }
    }
  }).catch(() => {
    deleteAllData()
  })
}

function initNewInterval() {
  interval.value = setInterval(checkToken, timeout)
}

function createLoginUrl() {
  const loginUrl = keycloak.value.createLoginUrl({ redirectUri: location }) + '&kc_locale=' + keycloakLocale + '&privacy_url=' + privacyurl
  window.keycloak.loginUrl = loginUrl
  return loginUrl
}

function createRegisterUrl() {
  const registerUrl = keycloak.value.createRegisterUrl({ redirectUri: location }) + '&kc_locale=' + keycloakLocale + '&privacy_url=' + privacyurl
  window.keycloak.registerUrl = registerUrl
  return registerUrl
}

function init() {
  createKeycloak()
  keycloak.value.onAuthLogout = deleteAllDataAfterLogout

  const einhellAuthData = JSON.parse(window.localStorage.getItem('einhell-auth-data'))
  if (einhellAuthData) {
    setOptions(einhellAuthData)
  }

  if (JsCookie.get('checkout_loggedin') && JSON.parse(JsCookie.get('checkout_loggedin')).Loggedin) {
    options.value.onLoad = 'check-sso'
    JsCookie.remove('checkout_loggedin', einhellAuthDataOptions)
  }

  options.value.checkLoginIframe = false

  keycloak.value.init(options.value).then(authenticated => {
    isAuthenticated.value = authenticated
    keycloak.value.onLoad = ''

    if (isAuthenticated.value) {
      window.keycloak.isAuthenticated = true
      window.localStorage.removeItem('einhell-login-data')
      writeDataToLocalStorage()
      setUsername()
      initNewInterval()
    } else {
      deleteAllData()
    }

  }).catch(() => {
    deleteAllData()
  })
}

onMounted(() => {
  if (!window.keycloak) {
    window.keycloak = {}
  }

  loadLanguageAsync().then(() => {
    init()
  })
})
</script>

<template v-if="keycloak">
  <template v-if="isAuthenticated">
    <li class="module-nav__item">
      <span class="icon-registration-before">{{ username }}</span>
      <div class="overlay">
        <div class="overlay__item">
          <a :href="dashboardurl"><span>{{ $t('dashboard') }}</span></a>
        </div>
        <div class="overlay__item">
          <a :href="keycloak.createAccountUrl({ redirectUri: location }) + '&kc_locale=' + keycloakLocale + '&privacy_url=' + privacyurl">
            <span>{{ $t('account') }}</span>
          </a>
        </div>
        <div class="overlay__item">
          <a :href="keycloak.createLogoutUrl({ redirectUri: location }) + '&kc_locale=' + keycloakLocale + '&privacy_url=' + privacyurl">
            <span>{{ $t('logout') }}</span>
          </a>
        </div>
      </div>
    </li>
  </template>
  <template v-else>
    <li class="module-nav__item">
      <a :href="createLoginUrl()" v-if="!isAuthenticated && keycloak && typeof keycloak.createLoginUrl === 'function'">
        <span class="icon-star-before">{{ $t('login') }}</span>
      </a>
    </li>
    <li class="module-nav__item">
      <a :href="createRegisterUrl()" v-if="typeof keycloak.createRegisterUrl === 'function'">
        <span class="icon-registration-before">{{ $t('register') }}</span>
      </a>
    </li>
  </template>
</template>
